import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { useDomCalculation } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import { IS_MAC } from '@sohanscript/lib/constants';
import { formatNumber } from '@sohanscript/lib/utils/format';
import { Kbd, KbdGroup } from '@sohanscript/ui/kbd';
import { Iphone } from '@sohanscript/ui/mock/iphone';
import { Safari } from '@sohanscript/ui/mock/safari';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	type ReactZoomPanPinchRef,
	TransformComponent,
	TransformWrapper,
	useControls,
	useTransformEffect,
} from 'react-zoom-pan-pinch';
import { Img } from '@/components/image';
import { Button } from '../ui/button';

function ZoomHandler() {
	const { zoomIn, zoomOut, state, setTransform } = useControls();
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const handler = (e: WheelEvent) => {
			e.preventDefault();
			e.stopPropagation();

			if (e.ctrlKey) {
				if (e.deltaY < 0) {
					zoomIn(0.5);
				} else {
					zoomOut(0.5);
				}
			} else {
				const { scale, positionX, positionY } = state;
				const wrapperHeight =
					el.parentElement?.parentElement?.clientHeight ?? 0;
				const contentHeight = el.clientHeight ?? 0;

				const maxPanY = Math.max(0, contentHeight * scale - wrapperHeight);
				const nextY = positionY - e.deltaY;

				const clampedY = Math.min(0, Math.max(nextY, -maxPanY));

				setTransform(positionX, clampedY, scale, 0, 'easeOut');
			}
		};

		// HACK: { passive: false } is the key — this lets preventDefault() work
		el.addEventListener('wheel', handler, { passive: false });
		return () => el.removeEventListener('wheel', handler);
	}, [zoomIn, zoomOut, setTransform, state]);

	return <div ref={ref} className="absolute inset-0" />;
}

function MainSlide({ src }: { src: string }) {
	const apiRef = useRef<ReactZoomPanPinchRef | null>(null);
	const [initialized, setInitialized] = useState(false);
	const [loaded, setLoaded] = useState(false);

	const fit = useCallback(() => {
		const api = apiRef.current;
		if (!api) return;

		const wrapper = api.instance.wrapperComponent;
		if (!wrapper) return;

		const img = wrapper.querySelector<HTMLImageElement>('img');
		if (!img?.complete) return;

		const wrapperHeight = wrapper.clientHeight;
		const { height: imgHeight } = img.getBoundingClientRect();

		// NOTE: Scale up when image is shorter than the safari container
		// so it fills the height exactly — no empty space below.
		// zoomIn uses: targetScale = currentScale * Math.exp(step)
		// So step = Math.log(targetScale / currentScale)
		if (imgHeight > 0 && imgHeight < wrapperHeight) {
			api.zoomIn(Math.log(wrapperHeight / imgHeight), 0);
		}
	}, []);

	useEffect(() => {
		if (!initialized || !loaded) return;

		// NOTE: rAF ensures layout is settled after image paint
		requestAnimationFrame(fit);
	}, [initialized, loaded, fit]);

	return (
		<TransformWrapper
			onInit={(ref) => {
				apiRef.current = ref;
				setInitialized(true);
			}}
			wheel={{
				disabled: true,
			}}
			velocityAnimation={{ inertia: 0.1 }}
		>
			<Controller />
			<TransformComponent wrapperClass="aspect-safari w-full cursor-grab overflow-clip active:cursor-grabbing">
				<ZoomHandler />
				<Img
					src={src}
					width={2048}
					className="w-full"
					onLoad={() => setLoaded(true)}
				/>
			</TransformComponent>
		</TransformWrapper>
	);
}

function Controller() {
	const api = useControls();
	const [scale, setScale] = useState(1);

	useTransformEffect(({ state }) => {
		setScale(state.scale);
	});

	return (
		<div className="corner-bevel absolute right-2 bottom-2 z-10 flex rounded-lg bg-background/20 backdrop-blur p-1 *:rounded-lg">
			<Button
				icon="lucide:zoom-in"
				aria-label={m.image_inspector_zoom_in()}
				disabled={scale === 8}
				variant="ghost"
				onClick={() => api.zoomIn(0.2)}
				size="icon-sm"
			/>
			<Button
				disabled={scale === 1}
				icon="lucide:zoom-out"
				aria-label={m.image_inspector_zoom_out()}
				variant="ghost"
				onClick={() => api.zoomOut(0.2)}
				size="icon-sm"
			/>
			<Button
				icon="lucide:rotate-ccw"
				aria-label={m.image_inspector_reset_zoom()}
				variant="ghost"
				onClick={() => api.resetTransform()}
				size="icon-sm"
			/>
		</div>
	);
}

type ThumbGalleryProps = {
	images: string[];
	transitionName?: string;
	url?: string;
	isMobile?: boolean;
};

export function ThumbGallery({
	images,
	transitionName,
	isMobile,
	url,
}: ThumbGalleryProps) {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [mainRef, mainApi] = useEmblaCarousel({
		watchDrag: false,
		watchResize: false,
	});
	const [thumbRef, thumbApi] = useEmblaCarousel({
		containScroll: 'keepSnaps',
		dragFree: true,
		axis: isMobile ? 'y' : 'x',
	});

	const onThumbClick = useCallback(
		(index: number) => {
			if (!mainApi || !thumbApi) return;
			mainApi.scrollTo(index);
		},
		[mainApi, thumbApi],
	);

	const onSelect = useCallback(() => {
		if (!mainApi || !thumbApi) return;
		setSelectedIndex(mainApi.selectedScrollSnap());
		thumbApi.scrollTo(mainApi.selectedScrollSnap());
	}, [mainApi, thumbApi]);

	useEffect(() => {
		if (!mainApi) return;
		onSelect();
		mainApi.on('select', onSelect);
		mainApi.on('reInit', onSelect);
		return () => {
			mainApi.off('select', onSelect);
			mainApi.off('reInit', onSelect);
		};
	}, [mainApi, onSelect]);

	const { height: mainDeviceHeight } = useDomCalculation({
		blockIds: ['main-device'],
		substract: false,
		dynamic: true,
	});

	if (!images.length) return null;

	return (
		<div
			className={cn('flex gap-3', isMobile ? 'h-min items-start' : 'flex-col')}
		>
			{isMobile ? (
				<Iphone className="w-72" id="main-device">
					<div ref={mainRef} className="overflow-hidden">
						<div className="flex">
							{images.map((src, i) => (
								<div
									key={src}
									style={
										i === 0 && transitionName
											? { viewTransitionName: transitionName }
											: undefined
									}
									className="relative flex-[0_0_100%] overflow-clip"
								>
									<Img src={src} className="size-full" />
								</div>
							))}
						</div>
					</div>
				</Iphone>
			) : (
				<Safari url={url} id="main-device">
					<div ref={mainRef} className="overflow-hidden">
						<div className="flex">
							{images.map((src, i) => (
								<div
									key={src}
									style={
										i === 0 && transitionName
											? { viewTransitionName: transitionName }
											: undefined
									}
									className="relative flex-[0_0_100%] overflow-clip"
								>
									<MainSlide src={src} />
								</div>
							))}
						</div>
					</div>
				</Safari>
			)}
			{!isMobile && <CanvasHints />}
			{images.length > 1 && (
				<div ref={thumbRef} className="corner-bevel overflow-hidden rounded-lg">
					<div
						style={{
							maxHeight: Math.max(mainDeviceHeight, 592),
						}}
						className={cn('flex gap-2', isMobile && 'flex-col')}
					>
						{images.map((src, i) => (
							<button
								key={src}
								type="button"
								aria-label={m.gallery_view_image({
									current: formatNumber(i + 1, { useGrouping: false }),
									total: formatNumber(images.length, { useGrouping: false }),
								})}
								onClick={() => onThumbClick(i)}
								className={cn(
									'corner-bevel relative min-w-0 shrink-0 cursor-pointer overflow-hidden rounded-lg border-2 transition-opacity',
									i === selectedIndex
										? 'border-accent opacity-100'
										: 'border-transparent opacity-70 hover:opacity-60',
									isMobile ? 'aspect-iphone w-24' : 'aspect-video h-20',
								)}
							>
								<Img
									src={src}
									width={160}
									className={cn('size-full object-cover object-top')}
								/>
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

export function CanvasHints() {
	return (
		<div className="inline-flex items-center gap-3 text-muted-foreground text-sm">
			<div className="flex items-center gap-1.5">
				<Kbd>
					<Iconify className="size-3.5" icon="lucide:mouse" aria-hidden />
				</Kbd>
				{m.pc_scroll_to_pan()}
			</div>
			<div className="flex items-center gap-1.5">
				<KbdGroup>
					<Kbd>{IS_MAC ? 'Cmd' : 'Ctrl'}</Kbd>
					<Kbd>+</Kbd>
					<Kbd>
						<Iconify className="size-3.5" icon="lucide:mouse" aria-hidden />
					</Kbd>
				</KbdGroup>
				{m.pc_zoom_in_out({ modifier: IS_MAC ? 'Cmd' : 'Ctrl' })}
			</div>
		</div>
	);
}
